import React from 'react';

export const getNote1_histologicalTumorType = () => (
  <div className="note note_1">
    <div className="title">
      <b>Histological tumour type</b>

      &nbsp; (Required)
    </div>
    <b> Reason/Evidentiary Support </b>

    <div className="paragraphs">
      <p className="paragraph">

        The vast majority (>95%) of prostate cancers are acinar adenocarcinomas.
        <sup>1</sup>
        {' '}
Other types of carcinoma are rarer but must be recorded if present, since some
        variants, such as ductal adenocarcinoma, small cell carcinoma, sarcomatoid carcinoma and
        urothelial-type adenocarcinoma, have a significantly poorer prognosis.
        <sup>1-6</sup>
        {' '}
The tumour type should be assigned in line with the 2016 World Health
        Organisation (WHO) classification and mixtures of different types should be indicated.
        <sup>1</sup>
        {' '}
Subtypes of prostate carcinoma are often identified in combination with acinar
        type carcinoma, and in such cases the tumour type should be classified according to the
        subtype.
      </p>
    </div>
    <b>

      WHO classification of tumours of the prostate
      <sup>a1</sup>
    </b>
    <table>
      <col width="90%" />
      <col width="10%" />
      <tr>
        <th>
          <b>Descriptor</b>
        </th>
        <th>
          <b>ICD-O codes</b>
        </th>
      </tr>

      <tr>
        <td>
          <b>Epithelial tumours</b>
        </td>
        <td />
      </tr>

      <tr>
        <td>
          <i>Glandular neoplasms</i>
        </td>
        <td />
      </tr>

      <tr>
        <td>Acinar adenocarcinoma</td>
        <td>8140/3</td>
      </tr>

      <tr>
        <td style={{ paddingLeft: '3rem' }}>Atrophic</td>
        <td />
      </tr>
      <tr>
        <td style={{ paddingLeft: '3rem' }}>Pseudohyperplastic</td>
        <td />
      </tr>
      <tr>
        <td style={{ paddingLeft: '3rem' }}>Microcystic</td>
        <td />
      </tr>
      <tr>
        <td style={{ paddingLeft: '3rem' }}>Foamy gland"</td>
        <td />
      </tr>
      <tr>
        <td style={{ paddingLeft: '3rem' }}>Mucinous (colloid)</td>
        <td> 8480/3</td>
      </tr>
      <tr>
        <td style={{ paddingLeft: '3rem' }}>Signet ring-like cell</td>
        <td>8490/3</td>
      </tr>
      <tr>
        <td style={{ paddingLeft: '3rem' }}>Pleomorphic giant cell</td>
        <td />
      </tr>
      <tr>
        <td style={{ paddingLeft: '3rem' }}>Sarcomatoid</td>
        <td>8572/3</td>
      </tr>

      <tr>
        <td>Prostatic intraepithelial neoplasia, high-grade</td>
        <td>8148/2</td>
      </tr>
      <tr>
        <td>Intraductal carcinoma</td>
        <td>8500/2</td>
      </tr>
      <tr>
        <td>Ductal adenocarcinoma</td>
        <td>8500/3</td>
      </tr>

      <tr>
        <td style={{ paddingLeft: '3rem' }}>Cribiform</td>
        <td>8201/3</td>
      </tr>

      <tr>
        <td style={{ paddingLeft: '3rem' }}>Papillary</td>
        <td>8260/3</td>
      </tr>

      <tr>
        <td style={{ paddingLeft: '3rem' }}>Solid</td>
        <td>8230/3</td>
      </tr>

      <tr>
        <td>Urothelial carcinoma</td>
        <td>8120/3</td>
      </tr>
      <tr>
        <td>
          <i>Squamous neoplasms</i>
        </td>
        <td />
      </tr>

      <tr>
        <td style={{ paddingLeft: '3rem' }}>Adenosquamous carcinoma</td>
        <td>8560/3</td>
      </tr>

      <tr>
        <td style={{ paddingLeft: '3rem' }}>Squamous cell carcinoma</td>
        <td>8070/3</td>
      </tr>

      <tr>
        <td>Basal cell carcinoma</td>
        <td>8147/3</td>
      </tr>

      <tr>
        <td>
          <b>Neuroendocrine tumours</b>
        </td>
        <td />
      </tr>

      <tr>
        <td>Adenocarcinoma with neuroendocrine differentiation</td>
        <td>8574/3</td>
      </tr>

      <tr>
        <td>Well-differentiated neuroendocrine tumour</td>
        <td>8240/3</td>
      </tr>

      <tr>
        <td>Small cell neuroendocrine carcinoma</td>
        <td>8041/3</td>
      </tr>

      <tr>
        <td>Large cell neuroendocrine carcinoma </td>
        <td>8013/3</td>
      </tr>
    </table>
    <p className="table-foter">

      a The morphology codes are from the International Classification of Diseases for Oncology
      (ICD-O). Behaviour is coded /0 for benign tumours; /1 for unspecified, borderline, or
      uncertain behaviour; /2 for carcinoma in situ and grade III intraepithelial neoplasia; and /3
      for malignant tumours.
    </p>
    <p className="copy-right-text" style={{ fontWeight: 'lighter' }}>

      © WHO/International Agency for Research on Cancer (IARC). Reproduced with permission
    </p>
    <br />
    <p>

      Urothelial carcinomas arising in the urinary bladder or urethra are dealt with in separate
      datasets; however, those rare urothelial carcinomas arising within the prostate are included
      in this dataset. Information on histological tumour type may be recorded at a specimen level
      or at a case level depending on local practice. The response type “No evidence of primary
      tumour” should only be used if specimen level reporting is utilised.
    </p>
    <hr />
    <p className="text-bold">References</p>
    <ol className="references">
      <li className="reference">
        <span>

          World Health Organization (2016).
          <i>
            {' '}

            World Health Organization (WHO) Classification of tumours. Pathology and genetics of the
            urinary system and male genital organs
          </i>

          . Humphrey PA, Moch H, Reuter VE, Ulbright TM, editors. IARC Press, Lyon, France.
        </span>
      </li>
      <li className="reference">
        <span>

          Christensen WN, Steinberg G, Walsh PC and Epstein JI (1991). Prostatic duct
          adenocarcinoma. Findings at radical prostatectomy. &nbsp;
          <i>Cancer</i>
          {' '}
67:2118-2124.
        </span>
      </li>
      <li className="reference">
        <span>

          Rubenstein JH, Katin MJ, Mangano MM, Dauphin J, Salenius SA, Dosoretz DE and Blitzer PH
          (1997). Small cell anaplastic carcinoma of the prostate: seven new cases, review of the
          literature, and discussion of a therapeutic strategy. &nbsp;
          <i>Am J Clin Oncol</i>
          {' '}
20:376-380.
        </span>
      </li>
      <li className="reference">
        <span>

          Dundore PA, Cheville JC, Nascimento AG, Farrow GM and Bostwick DG (1995). Carcinosarcoma
          of the prostate. Report of 21 cases. &nbsp;
          <i>Cancer</i>
          {' '}
76:1035-1042
        </span>
      </li>
      <li className="reference">
        <span>

          Osunkoya AO and Epstein JI (2007). Primary mucin-producing urothelial-type adenocarcinoma
          of prostate: report of 15 cases. &nbsp;
          <i>Am J Surg Pathol</i>
          {' '}
31:1323-1329.
        </span>
      </li>
      <li className="reference">
        <span>

          Curtis MW, Evans AJ and Srigley J (2005). Mucin-producing urothelial-type adenocarcinoma
          of prostate: report of two cases of a rare and diagnostically challenging entity. &nbsp;
          <i>Mod Pathol</i>
          {' '}
18:585-590.
        </span>
      </li>
    </ol>
  </div>
);

export const getNote2_histologicalGrade = () => (
  <div className="note note_2">
    <div className="title">
      <b>Histological grade</b>

      &nbsp; (Required and Recommended)
    </div>
    <b> Reason/Evidentiary Support </b>

    <div className="paragraphs">
      <p className="paragraph">

        The Gleason grading system is the foundation of grading for prostatic adenocarcinoma. The
        Gleason score is traditionally obtained by adding the two predominant Gleason patterns or
        doubling the pattern in cases with uniform grade. This was modified in the International
        Society of Urological Pathology (ISUP) 2005 revision by always including the highest grade
        in the Gleason score of needle biopsies, regardless of its amount.
        <sup>1</sup>
        {' '}
At a subsequent ISUP consensus conference in 2014, the Gleason system was
        further modified and many of the decisions taken at this meeting have been included in the
        4th edition of the WHO classification. It was decided that Gleason pattern 4 should include
        fused or poorly formed glands, glomerulations and all cribriform patterns of acinar
        adenocarcinoma. A grouping of the Gleason scores into 5 grade categories was proposed and
        this was endorsed by the ISUP Council (March 2015). Over the past decades Gleason scores
        below 6 have become less commonly used, especially on needle biopsies. There is also an
        understanding that Gleason score 7 tumours have a worse prognosis if there is a predominant
        pattern 4 (4+3) than if pattern 3 dominates (3+4).
      </p>
      <p className="paragraph">

        Both the Gleason score and the ISUP grade (Grade group) should always be reported for the
        sake of clarity.
      </p>
      <p className="paragraph">

        The ISUP consensus conference also recommended that the percentage of Gleason pattern 4 be
        reported in cases with ISUP grades 2 or 3. The rationale for this is to indicate if the
        tumour is bordering on the lower or higher ends of Gleason score 7. In some jurisdictions,
        Gleason score 7 tumours with ≤10% pattern 4 are considered for active surveillance.
        <sup>2</sup>
        {' '}
The percentage of Gleason pattern 4 and 5 is reported by some pathologists
        <sup>3</sup>
        {' '}
but this was not endorsed by the WHO classification working group. This element
        is thus optional.
      </p>
      <p className="paragraph">

        Depending on local practice, the different elements of grade data may be reported on either
        core or specimen level or as a composite (global) grade based on all cancer present in the
        biopsy cores or a combination of both.
      </p>
      <p className="paragraph">

        The grade groups and associated definitions are outlined in Table 1.
      </p>
      <p className="paragraph">

        Both the Gleason score and the ISUP grade (Grade group) should always be reported for the
        sake of clarity.
      </p>
    </div>
    <b>

      Table 1: ISUP grading system, core/needle biopsies and transurethral resection of the prostate
      (TURP) specimens
    </b>
    <table>
      <col width="20%" />
      <col width="20%" />
      <col width="60%" />
      <tr>
        <th>
          <b>ISUP grade (Grade group)</b>
        </th>
        <th>
          <b>Gleason score</b>
        </th>
        <th>
          <b>Definition</b>
        </th>
      </tr>

      <tr>
        <td>Grade 1</td>
        <td>2-6</td>
        <td>Only individual discrete well-formed glands</td>
      </tr>

      <tr>
        <td>Grade 2</td>
        <td>3+4=7</td>
        <td>

          Predominantly well-formed glands with lesser component (*) of poorly-
          formed/fused/cribriform glands
        </td>
      </tr>

      <tr>
        <td>Grade 3</td>
        <td>4+3=7</td>
        <td>

          Predominantly poorly-formed/fused/cribriform glands with lesser component (**) of
          well-formed glands
        </td>
      </tr>

      <tr>
        <td rowSpan="3">Grade 4</td>
        <td>4+4=8</td>
        <td>Only poorly-formed/fused/cribriform glands</td>
      </tr>

      <tr>
        <td>3+5=8</td>
        <td>

          Predominantly well-formed glands and lesser component (*) lacking glands (or with
          necrosis)
        </td>
      </tr>

      <tr>
        <td>5+3=8</td>
        <td>

          Predominantly lacking glands (or with necrosis) and lesser component (**) of well-formed
          glands
        </td>
      </tr>

      <tr>
        <td>Grade 5</td>
        <td>9-10</td>
        <td>

          Lack gland formation (or with necrosis) with or without poorly formed/fused/cribriform
          glands
        </td>
      </tr>
    </table>
    <p className="table-foter">

      * Any component of the high-grade pattern (i.e. even if less than 5%) is included in the
      grade.
      <br />

      ** The low-grade pattern is included in the grade only if it is at least 5%.
    </p>
    <hr />
    <p className="text-bold">References</p>
    <ol className="references">
      <li className="reference">
        <span>

          Epstein JI, Allsbrook WCJ, Amin MB and Egevad LL (2005). The 2005 International Society of
          Urological Pathology (ISUP) Consensus Conference on Gleason Grading of Prostatic
          Carcinoma. &nbsp;
          <i>Am J Surg Pathol</i>
          {' '}
29(9):1228–1242.
        </span>
      </li>
      <li className="reference">
        <span>

          Morash C, Tey R, Agbassi C, Klotz L, McGowan T, Srigley J and Evans A (2015). Active
          surveillance for the management of localized prostate cancer: Guideline recommendations.
          &nbsp;
          <i>Can Urol Assoc J</i>
          {' '}
9(5-6):171-178.
        </span>
      </li>
      <li className="reference">
        <span>

          Egevad L, Delahunt B, Samaratunga H and Srigley JR (2016). Utility of Reporting the
          Percentage of High-grade Prostate Cancer.&nbsp;
          <i>Eur Urol</i>
          {' '}
69(4):599-600.
        </span>
      </li>
    </ol>
  </div>
);

export const getNote3_tumorExtent = () => (
  <div className="note note_3">
    <div className="title">
      <b>Tumour extent</b>

      &nbsp; (Required)
    </div>
    <b> Reason/Evidentiary Support </b>

    <div className="paragraphs">
      <p className="paragraph">

        Number of biopsy cores positive for cancer and linear extent of cancer in the cores
        correlate with tumour volume, postoperative stage and outcome.
        <sup>1-5</sup>
        {' '}
Number of positive cores should be reported but may be difficult to determine
        because of fragmentation when multiple cores have been submitted together. The number of
        positive cores should not be greater than the number of cores taken (as specified in
        “Clinical Information”). Site specific labelling and single core submission facilitates the
        assessment of cancer extent.
        <sup>6</sup>
        {' '}
Linear extent should be reported and may be recorded either as millimetres
        cancer length or % cancer in each core or as a composite measure of cancer involvement in
        all cores.
        <sup>7</sup>
        {' '}
The methods for reporting of discontinuous cancer remain controversial. Whether
        intervening benign tissue is included or subtracted from the extent measurement may
        determine eligibility for active surveillance. A patient with ISUP grade 1 (Gleason score
        3+3=6) cancer in no more than 3 cores may be a candidate for active surveillance. In some
        protocols, if a positive core is greater than 50% involved by tumour, a patient would be
        ineligible for active surveillance.
        <sup>8</sup>
        {' '}
In such a case it is recommended that the tumour extent of a discontinuous
        cancer should be reported by both including and subtracting the intervening benign tissue,
        e.g. in a 20 mm core there are discontinuous foci of ISUP grade 1 cancer spanning a distance
        of 12 mm (60% linear extent) and measuring 1+1 mm (10% linear extent).
        <sup>8</sup>
      </p>
    </div>
    <hr />
    <p className="text-bold">References</p>
    <ol className="references">
      <li className="reference">
        <span>

          Kattan MW, Stapleton AM, Wheeler TM and Scardino PT (1997). Evaluation of a nomogram used
          to predict the pathologic stage of clinically localized prostate carcinoma.&nbsp;
          <i>Cancer</i>

          &nbsp; 79(3):528-537.
        </span>
      </li>
      <li className="reference">
        <span>

          Harnden P, Shelley MD, Naylor B, Coles B and Mason MD (2008). Does the extent of carcinoma
          in prostatic biopsies predict prostate-specific antigen recurrence? A systematic review.
          &nbsp;
          <i>Eur Urol</i>

          &nbsp; 54(4):728-739.
        </span>
      </li>
      <li className="reference">
        <span>

          Kattan MW, Eastham JA, Stapleton AM, Wheeler TM and Scardino PT (1998). A preoperative
          nomogram for disease recurrence following radical prostatectomy for prostate cancer.
          &nbsp;
          <i>J Natl Cancer Inst</i>

          &nbsp; 90(10):766–771.
        </span>
      </li>
      <li className="reference">
        <span>

          Stephenson AJ, Scardino PT, Eastham JA, Bianco FJ, Jr., Dotan ZA, Fearn PA and Kattan MW
          (2006). Preoperative nomogram predicting the 10-year probability of prostate cancer
          recurrence after radical prostatectomy. &nbsp;
          <i>J Natl Cancer Inst</i>

          &nbsp; 98(10):715-717.
        </span>
      </li>
      <li className="reference">
        <span>

          D'Amico AV, Moul J, Carroll PR, Sun L, Lubeck D and Chen MH (2003). Cancer-specific
          mortality after surgery or radiation for patients with clinically localized prostate
          cancer managed during the prostate-specific antigen era.&nbsp;
          <i>J Clin Oncol</i>

          &nbsp; 21(11):2163-2172.
        </span>
      </li>
      <li className="reference">
        <span>

          Srigley JR, Delahunt B, Egevad L, Samaratunga H and Evans AJ (2014). Optimising
          preanalytical factors affecting quality of prostate biopsies: the case for site specific
          labelling and single core submission.&nbsp;
          <i>Pathology</i>

          &nbsp; 46(7):579-580.
        </span>
      </li>
      <li className="reference">
        <span>

          Srigley JR, Humphrey PA, Amin MB, Chang SS, Egevad L, Epstein JI, Grignon DJ, McKiernan
          JM, Montironi R, Renshaw AA, Reuter VE and Wheeler TM (2009). Protocol for the examination
          of specimens from patients with carcinoma of the prostate gland.&nbsp;
          <i>Arch Pathol Lab Med</i>

          &nbsp; 133(10):1568-1576.
        </span>
      </li>
      <li className="reference">
        <span>

          Amin MB, Lin DW, Gore JL, Srigley JR, Samaratunga H, Egevad L, Rubin M, Nacey J, Carter
          HB, Klotz L, Sandler H, Zietman AL, Holden S, Montironi R, Humphrey PA, Evans AJ, Epstein
          JI, Delahunt B, McKenney JK, Berney D, Wheeler TM, Chinnaiyan AM, True L, Knudsen B and
          Hammond ME (2014). The critical role of the pathologist in determining eligibility for
          active surveillance as a management option in patients with prostate cancer: consensus
          statement with recommendations supported by the College of American Pathologists,
          International Society of Urological Pathology, Association of Directors of Anatomic and
          Surgical Pathology, the New Zealand Society of Pathologists, and the Prostate Cancer
          Foundation.&nbsp;
          <i>Arch Pathol Lab Med</i>

          &nbsp; 138(10):1387-1405.
        </span>
      </li>
    </ol>
  </div>
);

export const getNote4_perineuralInvasion = () => (
  <div className="note note_4">
    <div className="title">
      <b>Perineural invasion</b>

      &nbsp; (Recommended)
    </div>
    <b> Reason/Evidentiary Support </b>

    <div className="paragraphs">
      <p className="paragraph">

        The significance of perineural invasion in prostate core biopsy specimens is uncertain. Some
        studies show a correlation with extraprostatic extension (EPE) in the corresponding radical
        prostatectomy specimens or an association with adverse outcome in patients treated with
        radical prostatectomy or external beam radiation.
        <sup>1-3, 4-6</sup>
        {' '}
Other investigators have questioned prognostic value of biopsy
        perineural invasion in univariate or multivariate analyses.
        <sup>7-10</sup>
        {' '}
A systematic review of the literature concluded that the weight of evidence
        suggested that in clinically localised disease perineural invasion was a significant
        prognostic factor for EPE and subsequent local recurrence.
        <sup>11</sup>
        {' '}
In advanced disease perineural invasion is common and probably not of
        prognostic significance. It should also be noted that nerves are not necessarily present in
        biopsy material, therefore it is not always possible to assess the possibility of perineural
        invasion.
      </p>
    </div>
    <hr />
    <p className="text-bold">References</p>
    <ol className="references">
      <li className="reference">
        <span>

          Vargas SO, Jiroutek M and Welch WR et al (1999). Perineural invasion in prostate needle
          biopsy specimens: correlation with extraprostatic extension at resection.&nbsp;
          <i>Am J Clin Pathol</i>

          &nbsp; 111:223-228.
        </span>
      </li>
      <li className="reference">
        <span>

          de la Taille A, Rubin MA, Bagiella E, Olsson CA, Buttyan R, Burchardt T, Knight C, O'Toole
          KM and Katz AE (1999). Can perineural invasion on prostate needle biopsy predict prostate
          specific antigen recurrence after radical prostatectomy?&nbsp;
          <i>J Urol</i>

          &nbsp; 162(1):103-106.
        </span>
      </li>
      <li className="reference">
        <span>

          Sebo TJ, Cheville JC, Riehle DL, Lohse CM, Pankratz VS, Myers RP, Blute ML and Zincke H
          (2002). Perineural invasion and MIB-1 positivity in addition to Gleason score are
          significant preoperative predictors of progression after radical retropubic prostatectomy
          for prostate cancer.&nbsp;
          <i>Am J Surg Pathol</i>

          &nbsp; 26(4):431-439.
        </span>
      </li>
      <li className="reference">
        <span>

          Loeb S, Epstein JI, Humphreys EB and Walsh PC (2010). Does perineural invasion on prostate
          biopsy predict adverse prostatectomy outcomes?&nbsp;
          <i>BJU Int</i>

          &nbsp; 105:1510-1513.
        </span>
      </li>
      <li className="reference">
        <span>

          Quinn DI, Henshall SM and Brenner PC et al (2003). Prognostic significance of preoperative
          factors in localised prostate cancer treated with radical prostatectomy; importance of
          percentage of biopsies that contain tumor and the presence of biopsy perineural
          invasion.&nbsp;
          <i>Cancer</i>

          &nbsp; 97:1884-1893.
        </span>
      </li>
      <li className="reference">
        <span>

          Yu HH, Song DY and Tsai YY et al (2007). Perineural invasion affects biochemical
          recurrencefree survival in patients with prostate cancer treated with definitive external
          beam radiotherapy.&nbsp;
          <i>Urology</i>

          &nbsp; 70:111-116.
        </span>
      </li>
      <li className="reference">
        <span>

          Egan AJ and Bostwick DG (1997). Prediction of extraprostatic extension of prostate cancer
          based on needle biopsy findings: perineural invasion lacks significance on multivariate
          analysis.&nbsp;
          <i>Am J Surg Pathol</i>

          &nbsp; 21:1496-1500.
        </span>
      </li>
      <li className="reference">
        <span>

          O’Malley KJ, Pound CR, Walsh PC, Epstein JI and Partin AW (2002). Influence of biopsy
          perineural on long-term biochemical disease-free survival after radical
          prostatectomy.&nbsp;
          <i>Urology</i>

          &nbsp; 59:85-90.
        </span>
      </li>
      <li className="reference">
        <span>

          Bismar TA, Lewis JS, Vollmer RT and Humphrey PA (2003). Multiple measures of carcinoma
          extent versus perineural invasion in prediction of pathologic stage in a screening
          population.&nbsp;
          <i>Am J Surg Pathol</i>

          &nbsp; 27:432-440.
        </span>
      </li>
      <li className="reference">
        <span>

          Elharram M, Margel D, Finelli A, Trachtenberg J, Evans A, van der Kwast TH, Sweet JM and
          Fleshner N (2012). Perineural invasion on prostate biopsy does not predict adverse
          pathological outcome.&nbsp;
          <i>Can J Urol </i>

          &nbsp; 19(6):6567-6572.
        </span>
      </li>
      <li className="reference">
        <span>

          Harnden P, Shelley MD and Clements H et al (2007). The prognostic significance of
          perineural invasion in prostate cancer biopsies. A systemic review.&nbsp;
          <i>Cancer</i>

          &nbsp; 109:13-24
        </span>
      </li>
    </ol>
  </div>
);

export const getNote5_semicalVesicleEjaculatoryDuctInvasion = () => (
  <div className="note note_5">
    <div className="title">
      <b>Seminal vesicle/ejaculatory duct invasion</b>

      &nbsp; (Recommended)
    </div>
    <b> Reason/Evidentiary Support </b>

    <div className="paragraphs">
      <p className="paragraph">

        Seminal vesicle invasion (SVI) is rarely identified in needle biopsies cores, hence its
        absence does not need to be explicitly stated. However, if seminal vesicle/ejaculatory duct
        invasion is present it should be recorded and the following comments apply.
      </p>
      <p className="paragraph">

        SVI is defined as involvement of the muscular wall of the extraprostatic portion of the
        seminal vesicle.
        <sup>1</sup>
        {' '}
If possible seminal vesicle tissue is present (either unintentionally or
        intentionally, as in a targeted biopsy) and involved by carcinoma, this may be significant
        since it indicates that the tumour could be pT3b in the American Joint Committee on Cancer
        (AJCC)/Union for International Cancer Control (UICC) Staging system.
        <sup>2,3</sup>
        {' '}
However, assessment of SVI is problematic in needle biopsy specimens since it
        is impossible to reliably distinguish between extraprostatic seminal vesicle and
        intraprostatic seminal vesicle or ejaculatory duct tissue, therefore it is important not to
        over interpret invasion of the latter two structures as SVI since their involvement by
        tumour does not constitute pT3b disease. Unless one is dealing with a targeted seminal
        vesicle biopsy, it is recommended to report tumour involvement of such structures in a
        needle core biopsy as “seminal vesicle/ejaculatory duct invasion” rather than as SVI.
      </p>
    </div>
    <hr />
    <p className="text-bold">References</p>
    <ol className="references">
      <li className="reference">
        <span>

          Berney DM, Wheeler TM, Grignon DJ, Epstein JI, Griffiths DF, Humphrey PA, van der Kwast T,
          Montironi R, Delahunt B, Egevad L, Srigley JR and ISUP Prostate Cancer Group (2011).
          International Society of Urological Pathology (ISUP) consensus conference on handling and
          staging of radical prostatectomy specimens. Working group 4: seminal vesicles and lymph
          nodes.&nbsp;
          <i>Mod Pathol</i>

          &nbsp; 24:39-47.
        </span>
      </li>
      <li className="reference">
        <span>

          Amin M.B., Edge, S., Greene, F.L., Byrd, D.R., Brookland, R.K., Washington, M.K.,
          Gershenwald, J.E., Compton, C.C., Hess, K.R., Sullivan, D.C., Jessup, J.M., Brierley,
          J.D., Gaspar, L.E., Schilsky, R.L., Balch, C.M., Winchester, D.P., Asare, E.A., Madera,
          M., Gress, D.M., Meyer, L.R. (Eds.) (2017).&nbsp;
          <i>AJCC Cancer Staging Manual 8th ed.</i>

          &nbsp; Springer, New York.
        </span>
      </li>
      <li className="reference">
        <span>

          Brierley JD, Gospodarowicz MK, Whittekind C, editors.&nbsp;
          <i>UICC TNM Classification of Malignant Tumours, 8th Edition.</i>

          &nbsp; Wiley-Blackwell.
        </span>
      </li>
    </ol>
  </div>
);

export const getNote6_lymphovascularInvasion = () => (
  <div className="note note_6">
    <div className="title">
      <b>Lymphovascular invasion</b>

      &nbsp; (Recommended)
    </div>
    <b> Reason/Evidentiary Support </b>

    <div className="paragraphs">
      <p className="paragraph">

        Lymphovascular invasion (LVI) is rarely identified in needle biopsies cores, hence its
        absence does not need to be explicitly stated. However, if LVI is present it should be
        recorded and the following comments apply.
      </p>
      <p className="paragraph">

        Invasion of lymphatic or blood vessels (i.e. thin-walled endothelial-lined spaces) is
        uncommonly identified in needle core biopsy specimens and there is little published data on
        the significance of LVI specifically relating to prostate core biopsies. However, there is
        good evidence that LVI is a significant independent prognostic indicator of increased risk
        of recurrence post radical prostatectomy;
        <sup>1-4</sup>
        {' '}
therefore, if LVI is identified in a needle core it may well be significant
        and its presence should be recorded. The presence of LVI does not affect assignment of the
        AJCC/UICC T category.
      </p>
    </div>
    <hr />
    <p className="text-bold">References</p>
    <ol className="references">
      <li className="reference">
        <span>

          Herman CM, Wilcox GE, Kattan MW, Scardino PT and Wheeler TM (2000). Lymphovascular
          invasion as a predictor of disease progression in prostate cancer.&nbsp;
          <i>Am J Surg Pathol</i>

          &nbsp; 24(6):859–863.
        </span>
      </li>
      <li className="reference">
        <span>

          Cheng L, Jones TD, Lin H, Eble JN, Zeng G, Carr MD and Koch MO (2005). Lymphovascular
          invasion is an independent prognostic factor in prostatic adenocarcinoma.&nbsp;
          <i>J Urol</i>

          &nbsp; 174(6):2181–2185.
        </span>
      </li>
      <li className="reference">
        <span>

          Yee DS, Shariat SF, Lowrance WT, Maschino AC, Savage CJ, Cronin AM, Scardino PT and
          Eastham JA (2011). Prognostic significance of lymphovascular invasion in radical
          prostatectomy specimens.&nbsp;
          <i>BJU Int</i>

          &nbsp; 108:502-507.
        </span>
      </li>
      <li className="reference">
        <span>

          May M, Kaufmann O, Hammermann F and Siegsmund M (2007). Prognostic impact of
          lymphovascular invasion in radical prostatectomy specimens.&nbsp;
          <i>BJU Int </i>

          &nbsp; 99:539-544.
        </span>
      </li>
    </ol>
  </div>
);

export const getNote7_extraprostaticExtension = () => (
  <div className="note note_7">
    <div className="title">
      <b>Extraprostatic extension</b>

      &nbsp; (Required and Recommended)
    </div>
    <b> Reason/Evidentiary Support </b>

    <div className="paragraphs">
      <p className="paragraph">

        Extraprostatic extension (EPE) became accepted terminology at a 1996 consensus conference,
        and replaces earlier ambiguous terms such capsular penetration, perforation, or invasion.
        <sup>1</sup>
        {' '}
In radical prostatectomy specimens EPE is an independent prognostic indicator
        of increased risk of recurrence post radical prostatectomy and is important in assignment of
        the AJCC/UICC T category.
        <sup>2,3</sup>
        {' '}
There is limited data specifically on the significance of EPE in needle core
        biopsies given that it is relatively uncommonly identified; however, it may be occasionally
        be seen and should be reported when present since it indicates that the tumour is at least
        pT3a in the TNM system.
        <sup>4</sup>
        {' '}
In needle cores it is defined as tumour admixed with adipocytes, usually at the
        end of a biopsy core.
      </p>
      <p className="paragraph">

        It is recommended that the site of any EPE present is recorded since this information is
        useful for correlation with magnetic resonance imaging (MRI) results and may assist the
        urologist or radiation oncologist with the technical aspects of treatment planning.
      </p>
    </div>
    <hr />
    <p className="text-bold">References</p>
    <ol className="references">
      <li className="reference">
        <span>

          Sakr WA, Wheeler TM, Blute M, Bodo M, Calle-Rodrigue R, Henson DE, Mostofi FK, Seiffert J,
          Wojno K and Zincke H (1996). Staging and reporting of prostate cancer-sampling of the
          radical prostatectomy specimen.&nbsp;
          <i>Cancer</i>

          &nbsp; 78(2):366–368.
        </span>
      </li>
      <li className="reference">
        <span>

          Wheeler TM, Dillioglugil O, Kattan MW, Arakawa A, Soh S, Suyama K, Ohori M and Scardino PT
          (1998). Clinical and pathological significance of the level and extent of capsular
          invasion in clinical stage T1-2 prostate cancer.&nbsp;
          <i>Hum Pathol</i>

          &nbsp; 29(8):856–862.
        </span>
      </li>
      <li className="reference">
        <span>

          Epstein JI, Partin AW, Sauvageot J and Walsh PC (1996). Prediction of progression
          following radical prostatectomy. A multivariate analysis of 721 men with long-term
          follow-up.&nbsp;
          <i>Am J Surg Pathol </i>

          &nbsp; 20(3):286–292.
        </span>
      </li>
      <li className="reference">
        <span>

          Amin M.B., Edge, S., Greene, F.L., Byrd, D.R., Brookland, R.K., Washington, M.K.,
          Gershenwald, J.E., Compton, C.C., Hess, K.R., Sullivan, D.C., Jessup, J.M., Brierley,
          J.D., Gaspar, L.E., Schilsky, R.L., Balch, C.M., Winchester, D.P., Asare, E.A., Madera,
          M., Gress, D.M., Meyer, L.R. (Eds.) (2017).&nbsp;
          <i>AJCC Cancer Staging Manual 8th ed.</i>

          &nbsp; Springer, New York.
        </span>
      </li>
    </ol>
  </div>
);

export const getNote8_intraductalCarcinomaOfProstate = () => (
  <div className="note note_8">
    <div className="title">
      <b>Intraductal carcinoma of prostate</b>

      &nbsp; (Recommended)
    </div>
    <b>Reason/Evidentiary Support</b>

    <div className="paragraphs">
      <p className="paragraph">

        Intraductal carcinoma of the prostate (IDC-P) is an uncommon finding in needle biopsies
        cores, hence its absence does not need to be explicitly stated. However, if IDC-P is present
        it should be recorded and the following comments apply.
      </p>
      <p className="paragraph">

        IDC-P is usually associated with invasive prostate cancer, however, occasionally isolated
        IDC-P is found without invasive carcinoma; this latter situation is rare and beyond the
        scope of this dataset.
      </p>
      <p className="paragraph">

        IDC-P has been well characterised at the histological and molecular levels over the past
        decade and its clinical significance is now also better understood.
        <sup>1</sup>
        {' '}
The diagnosis of IDC-P is based on morphology and the key criteria include: 1)
        large calibre glands that are more than twice the diameter of normal non-neoplastic
        peripheral glands; 2) preserved (at least focally) basal cells identified on H&E staining
        (or with basal cell markers, such as p63, keratin 34βE12 and keratin 5/6, however, the use
        of immunohistochemistry to identify basal cells is optional, rather than mandatory, for the
        diagnosis of IDC-P); 3) significant nuclear atypia including enlargement and anisonucleosis;
        and 4) comedonecrosis, which is often but not always present.
        <sup>2-3</sup>
        {' '}
It is important to distinguish IDC-P from high grade prostatic
        intraepithelial neoplasia (HGPIN): compared to IDC-P, HGPIN has less architectural and
        cytological atypia, and cribriform HGPIN is rare.
      </p>
      <p className="paragraph">

        IDC-P is strongly associated with high volume, high grade invasive prostate carcinoma and
        metastatic disease, hence the presence of IDC-P in a biopsy, even if invasive carcinoma
        cannot be identified, mandates immediate repeat biopsy or definitive therapy (depending on
        the clinical situation).
        <sup>6-7</sup>
        {' '}
In a cohort treated with radiation +/- androgen deprivation therapy, the
        presence of IDC-P in the needle biopsy was an independent predictor of early biochemical
        recurrence and metastasis.
        <sup>8</sup>
      </p>
      <p className="paragraph">

        There was a strong consensus (82%) at the recent ISUP consensus meeting (Chicago 2014) that
        IDC-P should not be assigned an ISUP or Gleason grade.
        <sup>9</sup>
      </p>
    </div>
    <hr />
    <p className="text-bold">References</p>
    <ol className="references">
      <li className="reference">
        <span>

          Zhou M (2013). Intraductal carcinoma of the prostate: the whole story.&nbsp;
          <i>Pathology.</i>

          &nbsp; 45(6):533-539.
        </span>
      </li>
      <li className="reference">
        <span>

          Cohen RJ, Wheeler TM, Bonkhoff H and Rubin MA (2007). A proposal on the identification,
          histologic reporting, and implications of intraductal prostatic carcinoma.&nbsp;
          <i>Arch Pathol Lab Med</i>

          &nbsp; 131(7):1103-1109.
        </span>
      </li>
      <li className="reference">
        <span>

          Guo CC and Epstein JI (2006 ). Intraductal carcinoma of the prostate on needle biopsy:
          Histologic features and clinical significance.&nbsp;
          <i>Mod Pathol.</i>

          &nbsp; 19(12):1528-1535.
        </span>
      </li>
      <li className="reference">
        <span>

          Kovi J, Jackson MA and Heshmat MY (1985). Ductal spread in prostatic carcinoma.&nbsp;
          <i>Cancer</i>

          &nbsp; 56(7):1566-1573.
        </span>
      </li>
      <li className="reference">
        <span>

          McNeal JE and Yemoto CE (1996). Spread of adenocarcinoma within prostatic ducts and acini.
          Morphologic and clinical correlations.&nbsp;
          <i>Am J Surg Pathol</i>

          &nbsp; 20(7):802-814.
        </span>
      </li>
      <li className="reference">
        <span>

          Robinson BD and Epstein JI (2010). Intraductal carcinoma of the prostate without invasive
          carcinoma on needle biopsy: emphasis on radical prostatectomy findings.&nbsp;
          <i>J Urol</i>

          &nbsp; 184(4):1328-1333.
        </span>
      </li>
      <li className="reference">
        <span>

          Zhao T, Liao B, Yao J, Liu J, Huang R, Shen P, Peng Z, Gui H, Chen X, Zhang P, Zhu Y, Li
          X, Wei Q, Zhou Q, Zeng H and Chen N (2015). Is there any prognostic impact of intraductal
          carcinoma of prostate in initial diagnosed aggressively metastatic prostate cancer?&nbsp;
          <i>Prostate</i>

          &nbsp; 75(3):225-232.
        </span>
      </li>
      <li className="reference">
        <span>

          Van der Kwast T, Al Daoud N, Collette L, Sykes J, Thoms J, Milosevic M, Bristow RG, Van
          Tienhoven G, Warde P, Mirimanoff RO and Bolla M (2012). Biopsy diagnosis of intraductal
          carcinoma is prognostic in intermediate and high risk prostate cancer patients treated by
          radiotherapy.&nbsp;
          <i>Eur J Cancer</i>

          &nbsp; 48(9):1318-1325.
        </span>
      </li>
      <li className="reference">
        <span>

          Epstein JI, Egevad L, Amin MB, Delahunt B, Srigley JR and Humphrey PA (2015). The 2014
          International Society of Urological Pathology (ISUP) Consensus Conference on Gleason
          Grading of Prostatic Carcinoma: Definition of Grading Patterns and Proposal for a New
          Grading System.&nbsp;
          <i>Am J Surg Pathol</i>

          &nbsp; 40(2):244-52.
        </span>
      </li>
    </ol>
  </div>
);

export const getNote9_coexistentPathology = () => (
  <div className="note note_9">
    <div className="title">
      <b>Coexistent pathology</b>

      &nbsp; (Recommended)
    </div>
    <b> Reason/Evidentiary Support </b>

    <div className="paragraphs">
      <p className="paragraph">

        In some cases clinical management decisions may be aided by knowledge of coexisting
        pathology, such as HGPIN, glandular atypia suspicious for malignancy (atypical small acinar
        proliferation), granulomatous prostatitis etc.
      </p>
      <p className="paragraph">

        If there is carcinoma present, the presence of HGPIN is generally not significant, except
        perhaps occasionally in the situation where the carcinoma is of very limited extent. Even if
        no cancer is identified in the specimen, the significance of finding HGPIN in needle core
        biopsies has been controversial with some studies finding an increased risk for detection of
        prostatic adenocarcinoma in subsequent biopsies, while others did not.
        <sup>1,2</sup>
        {' '}
Recent studies, including one analysing data from a large Canadian cohort,
        found that this risk was related to the extent of HGPIN, i.e. the number of involved sites;
        only patients with multifocal HGPIN had a significantly increased risk of prostate cancer.
        <sup>3-5</sup>
        {' '}
Low grade prostatic intraepithelial neoplasia (PIN) should not be reported.
      </p>
      <p className="paragraph">

        Likewise, if there is carcinoma present in a specimen, the presence of glandular atypia
        suspicious for malignancy (atypical small acinar proliferation) is generally not
        significant, except perhaps occasionally in the situation where the carcinoma is of very
        limited extent. In specimens where there is no cancer identified but glandular atypia is
        present, the risk of carcinoma being present in subsequent biopsies is approximately 50%.
        <sup>6-9</sup>
      </p>
      <p className="paragraph">

        Active prostatitis and granulomatous prostatitis may cause a rise in serum prostate-specific
        antigen (PSA), although inflammatory lesions may coexist with carcinoma and it is important
        not to assume that their presence always accounts for an unexplained increase in a patient’s
        PSA.
      </p>
    </div>
    <hr />
    <p className="text-bold">References</p>
    <ol className="references">
      <li className="reference">
        <span>

          Epstein JI and Herawi M (2006). Prostatic needle biopsies containing prostatic
          intraepithelial neoplasia or atypical foci suspicious for carcinoma: implications for
          patient care.&nbsp;
          <i>J Urol</i>

          &nbsp;  175:820-834.
        </span>
      </li>
      <li className="reference">
        <span>

          Schlesinger C, Bostwick DG and Iczkowski KA (2005). High-grade intraepithelial neoplasia
          and atypical small acinar proliferation: predictive value for cancer in current practice.&nbsp;
          <i>Am J Surg Pathol</i>

          &nbsp;
           29:1201-1207.
        </span>
      </li>
      <li className="reference">
        <span>

          Merrimen JL, Jones G, Walker D, Leung CS, Kapusta LR and Srigley JR (2009). Multifocal
          high grade prostatic intraepithelial neoplasia is a significant risk factor for prostatic
          carcinoma.&nbsp;
          <i>J. Urology</i>

          &nbsp; 182:485-490.
        </span>
      </li>
      <li className="reference">
        <span>

          Merrimen JL, Jones G and Srigley JR (2010). Is high grade prostatic intraepithelial
          neoplasia still a risk factor for adenocarcinoma in the era of extended biopsy sampling?&nbsp;
          <i>Pathology</i>

          &nbsp;
           42:325-329.
        </span>
      </li>
      <li className="reference">
        <span>

          Akhavan A, Keith JD, Bastacky SI, Cai C, Wang Y, Nelson JB (2007). The proportion of cores
          with high-grade prostatic intreaepithelial neoplasia on extended pattern needle biopsy is
          significantly associated with prostatic cancer on site directed repeat biopsy.&nbsp;
          <i>BJU Int</i>

          &nbsp; 
          99:765- 769.
        </span>
      </li>
      <li className="reference">
        <span>

          Iczkowski KA, MacLennan GT and Bostwick DG (1997). Atypical small acinar proliferation
          suspicious for malignancy in prostate needle biopsies: clinical significance in 33 cases.&nbsp;
          <i>Am J Surg Pathol</i>

          &nbsp;
           21(12):1489-1495.
        </span>
      </li>
      <li className="reference">
        <span>

          Iczkowski KA, Chen HM, Yang XJ and Beach RA (2002). Prostate cancer diagnosed after
          initial biopsy with atypical small acinar proliferation suspicious for malignancy is
          similar to cancer found on initial biopsy.&nbsp;
          <i>Urology</i>

          &nbsp;  60(5):851-854.
        </span>
      </li>
      <li className="reference">
        <span>

          Mancuso PA, Chabert C, Chin P, Kovac P, Skyring T, Watt WH and Napaki S (2007). Prostate
          cancer detection in men with an initial diagnosis of atypical small acinar proliferation.&nbsp;
          <i>BJU Int</i>

          &nbsp;
           99(1):49-52.
        </span>
      </li>
      <li className="reference">
        <span>

          Cheville JC, Reznicek MJ and Bostwick DG (1997). The focus of "atypical glands,
          suspicious for malignancy" in prostatic needle biopsy specimens: incidence, histologic
          features, and clinical follow-up of cases diagnosed in a community practice.&nbsp;
          <i>Am J Clin Pathol</i>

          &nbsp;  108(6):633- 640.
        </span>
      </li>
    </ol>
  </div>
);
